import React from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';
import { Checkbox, Label } from '../ui';

import css from './FieldCheckbox.module.css';

/**
 * Final Form Field containing checkbox input with new shadcn/ui design
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.textClassName overwrite components own css.textRoot given to label
 * @param {string} props.id given to input
 * @param {string} props.name Name groups several checkboxes to an array of selected values
 * @param {string} props.value Checkbox needs a value that is passed forward when user checks the checkbox
 * @param {ReactNode} props.label
 * @returns {JSX.Element} Final Form Field containing checkbox input
 */
const FieldCheckbox = props => {
  const {
    rootClassName,
    className,
    textClassName,
    id,
    label,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  // This is a workaround for a bug in Firefox & React Final Form.
  // https://github.com/final-form/react-final-form/issues/134
  const handleOnChange = (input, event) => {
    const { onBlur, onChange } = input;
    onChange(event);
    onBlur(event);

    // If onChange has been passed as a props to FieldCheckbox
    if (rest.onChange) {
      rest.onChange(event);
    }
  };

  return (
    <div className={classes}>
      <Field type="checkbox" {...rest}>
        {fieldProps => {
          const { input, meta } = fieldProps;

          return (
            <div className={css.checkboxContainer}>
              <Checkbox
                id={id}
                checked={!!input.checked}
                onCheckedChange={(isChecked) => {
                  // Create a synthetic event that mimics the native checkbox event
                  const syntheticEvent = {
                    target: {
                      type: 'checkbox',
                      checked: isChecked,
                      name: input.name,
                      value: input.value
                    },
                    currentTarget: {
                      checked: isChecked
                    }
                  };
                  handleOnChange(input, syntheticEvent);
                }}
                disabled={rest.disabled}
              />
              <Label
                htmlFor={id}
                className={classNames(css.checkboxLabel, textClassName)}
              >
                {label}
              </Label>
            </div>
          );
        }}
      </Field>
    </div>
  );
};

export default FieldCheckbox;
